import { fmtNum, fmtRub, formatPeriod } from "@/lib/format";
import { formatMessage, reconciliationResultMessages } from "@/services/messages";

/**
 * Σ-strip — 4-cell KPI-полоса под header'ом aggregated-карточки (фича 024 ①).
 *
 * 4 ячейки: Источников / Σ объём / Σ стоимость / Период.
 * Пустые поля (null/0) допускают рендер прочерком — но компонент не делает
 * их опциональными в API, чтобы оставить решение на стороне родителя.
 */
export interface DiscrepancySigmaStripProps {
	/** Кол-во источников (строк-actSources/woSources). */
	sourcesCount: number;
	/** Кол-во уникальных файлов среди источников. */
	filesCount: number;
	/** Σ объёма по источникам (одна общая ед. изм.). */
	totalQuantity: number;
	/** Единица объёма (м³, кг, шт и т. д.). */
	unit: string | null;
	/** Σ стоимости по источникам в рублях. */
	totalCost: number;
	/** Все даты создания файлов источников (для расчёта периода). */
	periodDates: ReadonlyArray<string | Date | null | undefined>;
}

export function DiscrepancySigmaStrip({
	sourcesCount,
	filesCount,
	totalQuantity,
	unit,
	totalCost,
	periodDates,
}: DiscrepancySigmaStripProps) {
	const messages = reconciliationResultMessages.redesign;
	const inFiles = formatMessage(messages.sigma_in_files_template, { n: filesCount });
	const period = formatPeriod(periodDates);

	return (
		<div className="mx-4 mb-3 grid grid-cols-4 gap-px overflow-hidden rounded-md bg-slate-200 text-xs ring-1 ring-slate-200">
			<Cell label={messages.sigma_sources_label}>
				<span className="font-mono text-[15px] font-semibold tabular-nums text-slate-900">
					{sourcesCount}
				</span>{" "}
				<span className="text-[11px] font-normal text-slate-500">{inFiles}</span>
			</Cell>
			<Cell label={messages.sigma_volume_label}>
				<span className="font-mono text-[15px] font-semibold tabular-nums text-slate-900">
					{fmtNum(totalQuantity)}
				</span>
				{unit ? <span className="ml-1 text-[11px] font-normal text-slate-500">{unit}</span> : null}
			</Cell>
			<Cell label={messages.sigma_cost_label}>
				<span className="font-mono text-[15px] font-semibold tabular-nums text-red-700">
					{fmtRub(totalCost)}
				</span>
			</Cell>
			<Cell label={messages.sigma_period_label}>
				<span className="text-[13px] text-slate-700">{period || "—"}</span>
			</Cell>
		</div>
	);
}

function Cell({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div className="bg-white px-3 py-2">
			<div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
			<div className="mt-0.5">{children}</div>
		</div>
	);
}
